import * as THREE from 'three';
import { shaderMaterial } from '@react-three/drei';
import { extend } from '@react-three/fiber';

// --- Custom Hatch Shader Material ---
// style codes: 0 solid, 1 parallel, 2 crosshatch, 3 brick, 4 blocks, 5 dots
const HatchShaderMaterial = shaderMaterial(
    {
        color: new THREE.Color('black'),
        backgroundColor: new THREE.Color('white'),
        spacing: 10.0,
        thickness: 1.0,
        angle: Math.PI / 4, // 45 degrees
        style: 1.0,
    },
    // Vertex Shader
    `
    varying vec2 vUv;
    varying vec3 vPosition;
    void main() {
        vUv = uv;
        vPosition = position;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
    `,
    // Fragment Shader
    `
    uniform vec3 color;
    uniform vec3 backgroundColor;
    uniform float spacing;
    uniform float thickness;
    uniform float angle;
    uniform float style;
    varying vec2 vUv;
    varying vec3 vPosition;

    float lineMask(vec2 p, float w) {
        return step(abs(fract(p.y) - 0.5), w);
    }

    void main() {
        vec2 p = vPosition.xy * spacing;
        float c = cos(-angle), s = sin(-angle);
        vec2 r = vec2(c * p.x - s * p.y, s * p.x + c * p.y);
        float t = clamp(thickness, 0.01, 0.9);
        float ink = 0.0;

        if (style < 0.5) {
            ink = 1.0;
        } else if (style < 1.5) {
            ink = lineMask(r, t * 0.5);
        } else if (style < 2.5) {
            ink = max(lineMask(r, t * 0.5), lineMask(r.yx, t * 0.5));
        } else if (style < 3.5) {
            // Brick: horizontal courses + staggered vertical joints
            float row = floor(r.y);
            float mortarY = step(1.0 - t, fract(r.y));
            float xShifted = r.x + mod(row, 2.0) * 0.5;
            float mortarX = step(1.0 - t * 0.5, fract(xShifted)) * step(t, fract(r.y));
            ink = max(mortarY, mortarX);
        } else if (style < 4.5) {
            // Blocks: square grid with offset courses
            float row = floor(r.y);
            float mortarY = step(1.0 - t, fract(r.y));
            float xShifted = r.x + mod(row, 2.0) * 0.5;
            float mortarX = step(1.0 - t, fract(xShifted));
            ink = max(mortarY, mortarX * step(t, fract(r.y)));
        } else {
            // Dots: staggered stipple
            vec2 cell = floor(r);
            vec2 f = fract(r) - 0.5;
            f.x += mod(cell.y, 2.0) * 0.25 - 0.125;
            ink = step(length(f), t * 0.6);
        }

        gl_FragColor = mix(vec4(backgroundColor, 1.0), vec4(color, 1.0), ink);
    }
    `
);

extend({ HatchShaderMaterial });

// --- Grid Shader Material ---
const GridShaderMaterial = shaderMaterial(
    {
        color: new THREE.Color('black'),
        backgroundColor: new THREE.Color('white'),
        spacing: 0.6, // 600mm default
        thickness: 0.005,
    },
    // Vertex Shader
    `
    varying vec2 vUv;
    varying vec3 vPosition;
    void main() {
        vUv = uv;
        vPosition = position;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
    `,
    // Fragment Shader
    `
    uniform vec3 color;
    uniform vec3 backgroundColor;
    uniform float spacing;
    uniform float thickness;
    varying vec3 vPosition;

    void main() {
        // Grid pattern based on world X/Y
        float x = fract(vPosition.x / spacing);
        float y = fract(vPosition.y / spacing);
        
        // Thickness threshold
        float t = thickness / spacing;
        
        if (x < t || y < t) {
            gl_FragColor = vec4(color, 1.0);
        } else {
            gl_FragColor = vec4(backgroundColor, 1.0);
        }
    }
    `
);

extend({ GridShaderMaterial });


// Remove all of this and use module augmentation if needed instead of namespace JSX.
// Types for custom material can be provided via module augmentation or in a separate type file if needed.

/**
 * Hidden Line Wrapper Component
 * Renders the children with a white material (or hatch) and black edges.
 */
export function HiddenLineWrapper({
    children: _children,
    isSelected: _isSelected,
    isHovered: _isHovered,
    showHatch: _showHatch = false,
    hatchColor: _hatchColor = 'black',
    hatchBackground: _hatchBackground = 'white',
    hatchSpacing: _hatchSpacing = 5.0,
}: {
    children?: React.ReactNode;
    isSelected?: boolean;
    isHovered?: boolean;
    showHatch?: boolean;
    hatchColor?: string;
    hatchBackground?: string;
    hatchSpacing?: number;
}) {
    // If selected/hovered, we might want to override the look, 
    // but for "Hidden Line" style, usually selection is just a highlight color on the lines or a subtle fill.
    // For this implementation, we'll keep the white/hatch fill and use blue/orange for edges if selected.

    // const edgeColor = isSelected ? '#00aaff' : isHovered ? '#ffaa00' : 'black';
    // const edgeWidth = isSelected ? 2 : 1;

    return (
        <group>
            <mesh>
                {/* We need the geometry from the child to render the mesh with material */}
                {/* This wrapper assumes it's wrapping a mesh or geometry. 
                    Actually, it's better to use this as a replacement for the <mesh> in the parent component.
                    But if we want to wrap existing logic, we might need to pass geometry.
                */}
            </mesh>
        </group>
    );
}

/**
 * Helper to get the material for Plan View.
 * `hatch` (when given) carries material-driven pattern parameters resolved
 * from the document material registry (style/angle/spacing/colors).
 */
export interface HatchParams {
    style?: 'Solid' | 'Parallel' | 'Crosshatch' | 'Brick' | 'Blocks' | 'ANSI31' | 'Dots' | 'None';
    angle?: number;
    spacing?: number;
    thickness?: number;
    foreground?: string;
    background?: string;
}

const HATCH_STYLE_CODE: Record<string, number> = {
    Solid: 0,
    Parallel: 1,
    Crosshatch: 2,
    Brick: 3,
    Blocks: 4,
    Dots: 5,
    ANSI31: 1,
    None: 1,
};

export function PlanViewMaterial({
    showHatch = false,
    color = 'black',
    backgroundColor = 'white',
    spacing = 2.0,
    hatch,
}: {
    showHatch?: boolean;
    color?: string;
    backgroundColor?: string;
    spacing?: number;
    hatch?: HatchParams;
}) {
    if (showHatch) {
        const styleCode = hatch?.style && hatch.style !== 'None'
            ? HATCH_STYLE_CODE[hatch.style] ?? 1
            : 1;
        return (
            <hatchShaderMaterial
                color={hatch?.foreground ?? color}
                backgroundColor={hatch?.background ?? backgroundColor}
                spacing={hatch?.spacing ?? spacing}
                thickness={hatch?.thickness ?? 1.0}
                angle={hatch?.style === 'ANSI31' ? Math.PI / 4 : (hatch?.angle ?? Math.PI / 4)}
                style={styleCode}
            />
        );
    } else {
        return <meshBasicMaterial color={backgroundColor} polygonOffset polygonOffsetFactor={1} polygonOffsetUnits={1} />;
    }
}

export function CeilingGridMaterial({
    color = 'black',
    backgroundColor = 'white',
    spacing = 0.6
}: {
    color?: string;
    backgroundColor?: string;
    spacing?: number;
}) {
    return (
        <gridShaderMaterial
            color={color}
            backgroundColor={backgroundColor}
            spacing={spacing}
            thickness={0.002}
        />
    );
}

// Provide TypeScript type for JSX usage of custom HatchShaderMaterial
// See: https://docs.pmnd.rs/react-three-fiber/api/recipes#using-extend

// Instead, use module augmentation for types as recommended by TypeScript:

declare module "@react-three/fiber" {
    interface ThreeElements {
        hatchShaderMaterial: any;
        gridShaderMaterial: any;
    }
}
